// src/components/SocketContext.tsx
import React, { createContext, useContext, useEffect, useState } from "react";
import { io, Socket } from "socket.io-client";
import { useAuth } from "./AuthContext";
import { isSandbox } from "../../sandbox/session";
import { MockSocket } from "../../sandbox/mockSocket";

interface SocketContextType {
  socket: Socket | null;
}

const SocketContext = createContext<SocketContextType | undefined>(undefined);

export const SocketProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { isAuthenticated, user } = useAuth(); // Access the auth context to check authentication
  const [socket, setSocket] = useState<Socket | null>(null);
  useEffect(() => {
    if (isAuthenticated && user) {
      // Sandbox: a local object stands in for the server connection, so
      // nothing leaves the browser.
      const newSocket = isSandbox()
        ? (new MockSocket() as unknown as Socket)
        : io({
            auth: { userId: user.sub },
            reconnection: true,
            withCredentials: true,
            transports:
              process.env.NODE_ENV === "development" ? ["polling"] : ["websocket"],
            forceNew: true,
            upgrade: true,
          });

      setSocket(newSocket);

      newSocket.on("connect", () => {
        console.log("Socket connected");
        newSocket.emit("login", user.sub);
      });

      return () => {
        newSocket.disconnect();
        setSocket(null);
      };
    } else {
      setSocket(null);
    }
  }, [isAuthenticated, user]);

  return (
    <SocketContext.Provider value={{ socket }}>
      {children}
    </SocketContext.Provider>
  );
};

export const useSocket = () => {
  const context = useContext(SocketContext);
  if (!context) {
    throw new Error("useSocket must be used within a SocketProvider");
  }
  return context;
};
